<template>
  <div class="audio-player">
    <!-- Hidden audio -->
    <audio
      ref="audio"
      :src="src"
      @timeupdate="updateProgress"
      @loadedmetadata="loadMetadata"
      @ended="isPlaying = false"
    ></audio>

    <!-- TOP ROW -->
    <div class="controls-row">
      <!-- Play / Pause -->
      <button class="btn play-btn" @click="togglePlay">
        <span v-if="!isPlaying">▶</span>
        <span v-else>❚❚</span>
      </button>

      <!-- Mute -->
      <button class="btn mute-btn" @click="toggleMute">
        <span v-if="isMuted">🔇</span>
        <span v-else>🔊</span>
      </button>

      <!-- Playback Speed -->
      <select class="speed" v-model="playbackRate" @change="changeSpeed">
        <option v-for="rate in speeds" :key="rate" :value="rate">
          {{ rate }}x
        </option>
      </select>
    </div>

    <!-- PROGRESS BAR -->
    <div class="timeline-row">
      <span class="time">{{ formatTime(currentTime) }}</span>

      <div class="progress-container" @click="seekOnClick($event)">
        <div
          class="progress"
          :style="{ width: (currentTime / duration) * 100 + '%' }"
        ></div>
      </div>

      <span class="time">{{ formatTime(duration) }}</span>
    </div>
  </div>
</template>

<script>
import Vue from "vue";

export default Vue.extend({
  name: "AudioPlayer",

  props: {
    src: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      isPlaying: false,
      isMuted: false,
      currentTime: 0,
      duration: 0,
      playbackRate: 1,
      speeds: [0.5, 1, 1.25, 1.5, 2]
    };
  },

  methods: {
    togglePlay() {
      const audio = this.$refs.audio;
      if (audio.paused) {
        audio.play();
        this.isPlaying = true;
      } else {
        audio.pause();
        this.isPlaying = false;
      }
    },

    toggleMute() {
      const audio = this.$refs.audio;
      audio.muted = !audio.muted;
      this.isMuted = audio.muted;
    },

    loadMetadata() {
      const audio = this.$refs.audio;
      this.duration = audio.duration || 0;
    },

    updateProgress() {
      const audio = this.$refs.audio;
      this.currentTime = audio.currentTime;
    },

    seek() {
      const audio = this.$refs.audio;
      audio.currentTime = this.currentTime;
    },

    seekOnClick(event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const clickX = event.clientX - rect.left;
      const width = rect.width;
      const seekTime = (clickX / width) * this.duration;
      this.currentTime = seekTime;
      this.$refs.audio.currentTime = seekTime;
    },

    changeSpeed() {
      this.$refs.audio.playbackRate = this.playbackRate;
    },

    formatTime(seconds) {
      if (!seconds) return "0:00";
      const m = Math.floor(seconds / 60);
      const s = Math.floor(seconds % 60)
        .toString()
        .padStart(2, "0");
      return `${m}:${s}`;
    }
  }
});
</script>

<style>
.audio-player {
  width: 100%;
  background: var(--v-white-base);
  color: #fff;
  border-radius: 16px;
  padding: 20px;
  font-family: "Segoe UI", sans-serif;
  box-shadow: 0 8px 20px var(--v-white-darken1);
}

.controls-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 15px;
  margin-bottom: 20px;
}

.btn {
  background: linear-gradient(
    135deg,
    var(--v-primary-lighten4),
    var(--v-primary-base)
  );
  border: none;
  border-radius: 50%;
  padding: 14px;
  font-size: 18px;
  cursor: pointer;
}

.btn:hover {
  background: linear-gradient(
    135deg,
    var(--v-primary-base),
    var(--v-primary-lighten4)
  );
  box-shadow: 0 4px 20px var(--v-white-darken1);
}

.play-btn,
.mute-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  color: white;
}

.speed {
  padding: 6px;
  border-radius: 8px;
  border: 2px solid var(--v-primary-base);
  cursor: pointer;
}

.speed:hover {
  box-shadow: 0 4px 20px var(--v-white-darken1);
}

.timeline-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.time {
  font-size: 12px;
  color: #aaa;
  min-width: 40px;
  text-align: center;
}

.progress-container {
  flex: 1;
  height: 8px;
  background: var(--v-white-darken2);
  border-radius: 4px;
  cursor: pointer;
  position: relative;
}

.progress {
  height: 100%;
  background: linear-gradient(
    135deg,
    var(--v-primary-lighten4),
    var(--v-primary-base)
  );
  border-radius: 4px;
  transition: width 0.1s linear;
}
</style>
